/**
 * PatientRow
 *
 * Patient list row with 8 columns:
 * Paciente | Procedimento | Profissional | Prioridade | Tempo espera | Sugestões | Status | Ações
 *
 * The "Sugestões" column shows the best slot date/time (clickable to open drawer).
 * The "Ações" column has an "Oferecer" button when a best slot is available.
 */
import React from 'react';
import { Chip, IconButton, Tooltip, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  PATIENT_STATUS_LABELS,
  PATIENT_STATUS_COLORS,
  PRIORITY_LABELS,
  PRIORITY_COLORS,
} from '../constants/waitingListConstants';
import DemoTag from '../../../components/demo-data/DemoTag';
import { isDemoRecord } from '../../../components/demo-data/demoData';
import styles from '../WaitingListV2Page.module.css';

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').filter(w => w.length > 2).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

function getAvatarColor(priority) {
  if (priority === 'HIGH') return { bg: '#fef2f2', color: '#e11d48' };
  if (priority === 'LOW') return { bg: '#f0fdf4', color: '#16a34a' };
  return { bg: '#eff6ff', color: '#2563eb' };
}

/** Formats "2026-07-06" → "06/07" */
function formatShortDate(dateStr) {
  if (!dateStr) return '';
  try {
    const [, month, day] = dateStr.split('-');
    return `${day}/${month}`;
  } catch {
    return dateStr;
  }
}

/** "2026-11-04 10:12:00" → "04/11 às 10:12" */
function formatShortDateTime(dateTimeStr) {
  if (!dateTimeStr) return '';
  const [date, time] = String(dateTimeStr).split(' ');
  return time ? `${formatShortDate(date)} às ${time.slice(0, 5)}` : formatShortDate(date);
}

/** Última oferta de horário: quem ofereceu e quando (sem registro, só a data do último aviso). */
function lastOfferInfo(lastOffer, item) {
  if (lastOffer?.createdAt) {
    const who = lastOffer.createdByName ? ` por ${String(lastOffer.createdByName).split(' ')[0]}` : '';
    return {
      text: `Oferecido ${formatShortDateTime(lastOffer.createdAt)}${who}`,
      title: `Horário oferecido: ${formatShortDateTime(lastOffer.offeredDate)}${lastOffer.createdByName ? `, por ${lastOffer.createdByName}` : ''}`,
    };
  }
  if (item?.lastNotified) {
    return { text: `Último aviso ${formatShortDateTime(item.lastNotified)}`, title: '' };
  }
  return null;
}

function PatientRow({
  item,
  patient,
  procedure,
  resource,
  suggestionsCount = 0,
  bestSlot = null,
  onViewSuggestions,
  onOfferBestSlot,
  onDelete,
  onEdit,
  lastOffer = null,
}) {
  const offerInfo = lastOfferInfo(lastOffer, item);
  const waitingDays = item.createdAt
    ? Math.floor((Date.now() - new Date(item.createdAt).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  const statusColor = PATIENT_STATUS_COLORS[item.status] || '#9E9E9E';
  const statusLabel = PATIENT_STATUS_LABELS[item.status] || item.status;
  const priorityColor = PRIORITY_COLORS[item.priority] || '#FF9800';
  const priorityLabel = PRIORITY_LABELS[item.priority] || 'Média';
  const avatarColors = getAvatarColor(item.priority);

  const hasBestSlot = bestSlot && bestSlot.slotDate;
  const bestDateLabel = hasBestSlot ? formatShortDate(bestSlot.slotDate) : null;
  const bestTimeLabel = hasBestSlot && bestSlot.slotTime ? ` às ${bestSlot.slotTime}` : '';

  return (
    <div className={styles.patientRow}>
      {/* 1. Paciente */}
      <div className={styles.patientInfo}>
        <div
          className={styles.patientAvatar}
          style={{ background: avatarColors.bg, color: avatarColors.color }}
        >
          {getInitials(patient?.name)}
        </div>
        <div className={styles.patientNameGroup}>
          <span className={styles.patientName}>
            {patient?.name || 'Paciente não encontrado'}
          </span>
          <span className={styles.patientPhone}>
            {patient?.phone || ''}
          </span>
          {isDemoRecord(item) && <span style={{ alignSelf: 'flex-start' }}><DemoTag /></span>}
        </div>
      </div>

      {/* 2. Procedimento */}
      <span className={styles.patientProcedure}>
        {procedure?.Label || '—'}
      </span>

      {/* 3. Profissional preferido */}
      <span className={styles.patientProcedure}>
        {resource?.Label || 'Qualquer'}
      </span>

      {/* 4. Prioridade */}
      <Chip
        label={priorityLabel}
        size="small"
        sx={{
          backgroundColor: `${priorityColor}15`,
          color: priorityColor,
          border: `1px solid ${priorityColor}30`,
          fontSize: '0.72rem',
          height: 24,
          fontWeight: 600,
        }}
      />

      {/* 5. Tempo de espera */}
      <span className={styles.patientWaiting}>
        {waitingDays} {waitingDays === 1 ? 'dia' : 'dias'}
      </span>

      {/* 6. Sugestões — shows best slot date/time, clickable to open drawer */}
      {suggestionsCount > 0 ? (
        <div
          style={{ cursor: 'pointer' }}
          onClick={(e) => { e.stopPropagation(); onViewSuggestions?.(item); }}
        >
          {hasBestSlot ? (
            <>
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' }}>
                {bestDateLabel}{bestTimeLabel}
              </div>
              <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 1 }}>
                {suggestionsCount === 1 ? '1 horário livre · ver' : `${suggestionsCount} horários livres · ver todos`}
              </div>
            </>
          ) : (
            <span className={styles.suggestionsChip}>
              <CalendarMonthIcon sx={{ fontSize: '0.85rem' }} />
              {suggestionsCount} horário{suggestionsCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      ) : (
        <span className={styles.suggestionsChipEmpty}>—</span>
      )}

      {/* 7. Status (+ última oferta: quem ofereceu e quando) */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, minWidth: 0 }}>
        <Chip
          label={statusLabel}
          size="small"
          sx={{
            backgroundColor: `${statusColor}15`,
            color: statusColor,
            border: `1px solid ${statusColor}30`,
            fontSize: '0.72rem',
            height: 24,
            fontWeight: 500,
          }}
        />
        {offerInfo && (
          <span
            title={offerInfo.title || undefined}
            style={{ fontSize: '0.68rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}
          >
            {offerInfo.text}
          </span>
        )}
      </div>

      {/* 8. Ações */}
      <div className={styles.patientActions}>
        {hasBestSlot && (
          <Button
            size="small"
            variant="contained"
            onClick={(e) => { e.stopPropagation(); onOfferBestSlot?.(item, bestSlot); }}
            sx={{
              background: 'var(--color-blue-main, #5A80FB)',
              color: '#fff',
              fontSize: '0.75rem',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              boxShadow: 'none',
              padding: '5px 14px',
              minWidth: 0,
              letterSpacing: '0.1px',
              '&:hover': {
                background: 'var(--color-main-dark, #26347B)',
                boxShadow: '0 2px 8px rgba(90,128,251,0.3)',
              },
            }}
          >
            Oferecer
          </Button>
        )}
        <Tooltip title="Editar">
          <IconButton size="small" onClick={() => onEdit?.(item)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remover">
          <IconButton size="small" onClick={() => onDelete?.(item)} sx={{ color: '#94a3b8' }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
}

export default PatientRow;
